/**
 * US city name cleanup for filters + save paths.
 * Not a full gazetteer — casing/junk strip locally; geocode in cleanup scripts.
 */

const SMALL_WORDS = new Set([
  "a",
  "an",
  "and",
  "at",
  "by",
  "for",
  "from",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
]);

const ZIP_TRAILING = /\s+\d{5}(?:-\d{4})?\s*$/i;
const STATE_TRAILING = /\s+[A-Z]{2}\s*$/i;

/** Drop trailing zip / 2-letter state accidentally glued into city. */
export const stripJunkFromCity = (input?: string | null): string => {
  let value = String(input ?? "").trim().replace(/\s+/g, " ");
  if (!value) return "";
  value = value.replace(ZIP_TRAILING, "").trim();
  // Only strip trailing state if there's still a city name left.
  const withoutState = value.replace(STATE_TRAILING, "").trim();
  if (withoutState && withoutState !== value) {
    value = withoutState;
  }
  return value;
};

const titleCaseWord = (word: string, index: number): string => {
  if (!word) return word;

  // Preserve simple hyphenated parts (Wilkes-Barre)
  if (word.includes("-")) {
    return word
      .split("-")
      .map((part, i) => titleCaseWord(part, index + i))
      .join("-");
  }

  const lower = word.toLowerCase();
  if (index > 0 && SMALL_WORDS.has(lower)) return lower;

  // McDonald / MacArthur
  const mc = lower.match(/^(ma?c)([a-z].*)$/i);
  if (mc) {
    return (
      mc[1].charAt(0).toUpperCase() +
      mc[1].slice(1).toLowerCase() +
      mc[2].charAt(0).toUpperCase() +
      mc[2].slice(1).toLowerCase()
    );
  }

  // O'Brien
  const oPrime = lower.match(/^(o')([a-z].*)$/i);
  if (oPrime) {
    return `O'${oPrime[2].charAt(0).toUpperCase()}${oPrime[2].slice(1)}`;
  }

  // St. / Ft.
  if (/^(st|ft)\.?$/i.test(lower)) {
    return `${lower.replace(/\./, "").charAt(0).toUpperCase()}${lower.slice(1, 2)}.`;
  }

  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * Trim, strip junk, US-style title case for city names.
 * Returns null when empty.
 */
export const normalizeUsCityName = (
  input?: string | null,
): string | null => {
  const stripped = stripJunkFromCity(input);
  if (!stripped) return null;

  // Already looks like a proper multi-word title case with mixed case — still
  // normalize so "NEW HAVEN" and "new haven" converge.
  const titled = stripped
    .split(/\s+/)
    .map((word, index) => titleCaseWord(word, index))
    .join(" ");

  return titled || null;
};
